/**
 * La fiche d'un enfant telle que l'équipe la lit : ses sept lignes, remises
 * dans le modèle du parent (`depuisLignes`), avec ce qui l'entoure — sa
 * classe de l'année, son parent, les personnes autorisées du compte, le
 * journal des autorisations d'image.
 *
 * Partagée par Consulter et Modifier (Gestion › Enfants) : les deux écrans
 * relisent la base à chaque ouverture, et voient donc la même fiche.
 */

import { db } from '../../../../lib/payments/db'
import type { ClasseDuCentre } from '../../../../lib/classes/regles'
import { classeDeLEnfant } from '../../../../lib/classes/gateway.supabase'
import { depuisLignes, type Fiche, type LignesLues } from '../../../../components/forms/kid/fiche'
import type { ReperesEquipe } from './regles'
import type { EvenementConsentement, PersonneAutorisee } from './Onglets'

export interface ParentDeLEnfant {
  id: string
  nom: string | null
  prenom: string | null
  email: string | null
  telephone: string | null
  telephone2: string | null
  nnational: string | null
  adresse: string | null
  cpostal: string | null
  localite: string | null
  account_type: string | null
}

export interface LectureFiche {
  lignes: LignesLues
  fiche: Fiche
  reperes: ReperesEquipe
  /** La classe de l'année scolaire (`kid_classes`), ou `null`. */
  classe: ClasseDuCentre | null
  parent: ParentDeLEnfant | null
  /** Les registres nationaux des frères et sœurs : une fiche ne peut pas reprendre le leur. */
  nrnFratrie: string[]
  /** Toutes les personnes autorisées du compte, cochées ou non pour cet enfant. */
  personnes: PersonneAutorisee[]
  journal: EvenementConsentement[]
}

export async function lireFicheEquipe(kidId: string, annee: number): Promise<LectureFiche> {
  const unique = (table: string) => db.from(table).select('*').eq('kid_id', kidId).maybeSingle()
  const [kidRes, health, allergies, activities, departure, inclusion, imageRights, journalRes, classe] = await Promise.all([
    db.from('kids').select('*').eq('id', kidId).single(),
    unique('kid_health'),
    unique('kid_allergies'),
    unique('kid_activities'),
    unique('kid_departure'),
    unique('kid_inclusion'),
    unique('kid_image_rights'),
    db.from('kid_consent_events')
      .select('id, occurred_at, scope, value, actor_user_id, source')
      .eq('kid_id', kidId)
      .order('occurred_at', { ascending: false })
      .limit(30),
    classeDeLEnfant(kidId, annee).catch(() => null),
  ])
  if (kidRes.error) throw kidRes.error
  for (const r of [health, allergies, activities, departure, inclusion, imageRights]) {
    if (r.error) throw r.error
  }
  const kid = kidRes.data as LignesLues['kids'] & { user_id: string }

  /* Le parent, ses autres enfants et ses personnes autorisées. */
  const [parentRes, fratrieRes, personnesRes] = await Promise.all([
    db.from('users')
      .select('id, nom, prenom, email, telephone, telephone2, nnational, adresse, cpostal, localite, account_type')
      .eq('id', kid.user_id)
      .maybeSingle(),
    db.from('kids').select('id, n_national').eq('user_id', kid.user_id).neq('id', kidId),
    db.from('authorized_persons')
      .select('id, first_name, last_name, phone_number, relationship')
      .eq('user_id', kid.user_id)
      .order('first_name'),
  ])

  const lignes: LignesLues = {
    kids: kid,
    health: health.data,
    allergies: allergies.data,
    activities: activities.data,
    departure: departure.data,
    inclusion: inclusion.data,
    image_rights: imageRights.data,
  }
  const fiche = depuisLignes(lignes)
  fiche.identite.classeId = classe?.id ?? ''

  return {
    lignes,
    fiche,
    reperes: { formulaireMedication: !!(health.data as { medication_form_sent?: boolean } | null)?.medication_form_sent },
    classe,
    parent: (parentRes.data as ParentDeLEnfant | null) ?? null,
    nrnFratrie: ((fratrieRes.data ?? []) as { n_national: string | null }[])
      .map(k => k.n_national ?? '')
      .filter(Boolean),
    personnes: (personnesRes.data ?? []) as PersonneAutorisee[],
    journal: (journalRes.data ?? []) as EvenementConsentement[],
  }
}
